import { validationLabels } from '../components/form/validation-labels';
import { isEnterPressed } from './keyboard';
import { UIHint } from '../components/index';
import type { UIField, UIForm, UIData, UIDropdown } from '../components/index';
import {
    FormatterFn,
    FormValidatorRulesCustom,
    FormValidatorRule,
    FormValidatorRules,
    HTMLInputSelectOrTextElement,
    NullOr,
    PatternKey,
    RuleFn,
    UndefinedOr,
    ValidationPatterns,
    EventHandler,
    FormValidatorResponse,
    UpdateFormEventPayload,
    Pattern,
} from './utils.type';
import type { TranslationLabels } from '../types/labels.type';
import { runInNextFrame } from './helpers';

/**
 * @memberof SharedComponents
 * @alias FormValidator
 * @classdesc Class for form validator, it implements native browser's validation.
 */
export class FormValidator {
    private readonly _form: HTMLFormElement;
    private readonly errorPlacementCallback: EventHandler;
    private readonly _RULES: FormValidatorRulesCustom;
    private _beforeSubmit: UndefinedOr<() => void>;
    private static readonly AnnouncementTimeout = 100;
    private submitAttempted = false;

    /**
     * @type {string}
     */
    static readonly UIValidationErrorType: string = 'ui-validation';

    /**
     * Default validation event name.
     * @type {string}
     */
    static readonly DEFAULT_EVENT_NAME: string = 'change';

    /**
     * Default invalid event name.
     * @type {string}
     */
    static readonly INVALID_EVENT_NAME: string = 'invalid';

    /*
     * Input types that trigger native implicit form submission on ENTER.
     * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#implicit-submission
     */
    static readonly IMPLICIT_SUBMIT_INPUT_TYPES = [
        'date',
        'datetime-local',
        'email',
        'month',
        'number',
        'password',
        'search',
        'tel',
        'text',
        'time',
        'url',
        'week',
    ];

    /**
     * @param {HTMLFormElement} form - HTML form element from to be validated.
     */
    constructor(form: HTMLFormElement) {
        this._form = form;

        Object.defineProperty(form, 'validator', {
            value: this,
            writable: true,
        });

        // Errors
        this.errorPlacementCallback = this.drawErrors;
        // Rules
        this._RULES = this.getRulesConfig();
        this.initCustomRules();
        // Events
        this.initEvents();

        // Register validation event listeners synchronously so that any
        // form submission — including programmatic ones — is handled immediately.
        this.validate();

        // Defer the initial required-state resolution by a frame so page-load
        // scripts can adjust `required` before it is cached in data-required.
        // This covers the gap where a hidden field is shown programmatically
        // (e.g. details.open = true) when resolveElementsRequirability would
        // not be called otherwise.
        runInNextFrame(() => {
            this.resolveElementsRequirability();
        });
    }

    // Add custom rules
    initCustomRules = () => {
        this.addCustomRule('fieldMismatch', this.getFieldMismatchRule());
        this.addCustomRule('countCheckboxes', this.getCountCheckboxesRule());
        this.addCustomRule('requiredMultiple', this.getRequiredMultipleRule());
        this.addCustomRule('remoteValidation', this.getRemoteValidationRule());
        this.addCustomRule(
            'onlyOneOfValidation',
            this.getOnlyOneOfValidationRule()
        );
        this.addCustomRule('maxfilesize', this.getMaxFileSizeRule());
    };

    // Add event listeners
    initEvents = () => {
        this.getForm().addEventListener('form-update', this.handleFormUpdate);
        this.getForm().addEventListener('submit', this.handleSubmit);
        this.getForm().addEventListener('keydown', this.handleKeyDown);
        this.getForm().addEventListener('input', this.handleInputEvent);
        this.getForm().addEventListener('pointerdown', this.handlePointerDown);
        this.getForm().addEventListener('change', this.handleChangeEvent);
    };

    drawErrors = (event?: Event) => {
        // Don't show default error message tooltip.
        event?.preventDefault();

        const field =
            event?.target && event.target instanceof Element
                ? event?.target.closest<UIField>('ui-field')
                : null;

        if (!field) {
            return;
        }
        const invalidElementsCount = field.querySelectorAll(':invalid').length;

        field
            .querySelectorAll<HTMLInputSelectOrTextElement>(
                'input:not(.-utility),select,textarea'
            )
            .forEach((element: HTMLInputSelectOrTextElement) => {
                // Opt-in deferral: elements marked with `data-defer-validation`
                // don't draw their error until the first submit attempt.
                if (
                    !this.submitAttempted &&
                    element.hasAttribute('data-defer-validation')
                ) {
                    return;
                }

                // Native selects inside ui-dropdown are display:none (willValidate=false).
                // Treat them as valueMissing when required and value is empty, mirroring native behaviour.
                const isDropdownSelect =
                    element.localName === 'select' &&
                    !!element.closest<UIDropdown>('ui-dropdown');
                const isInvalidDropdownSelect =
                    isDropdownSelect &&
                    (element as HTMLSelectElement).required &&
                    (element as HTMLSelectElement).value === '';

                if (
                    (!element?.willValidate && !isDropdownSelect) ||
                    !this.shouldFormValidate()
                ) {
                    if (field && invalidElementsCount === 0) {
                        field.removeErrors(true);
                    }
                    return;
                }

                const results = [];
                for (const rule in element.dataset) {
                    if (
                        element.dataset?.hasOwnProperty(rule) &&
                        rule.endsWith('Rule')
                    ) {
                        const suffix = this.prepareSuffix(rule);
                        const validityName = this.getRuleNameBySuffix(suffix);
                        const ruleCustom = this.resolveCustomRule(validityName);
                        if (typeof ruleCustom === 'function') {
                            results.push(ruleCustom(element));
                        }
                    }
                }

                const validCustom = [].every.call(results, (e) => !!e);

                const customBtn = field.querySelector(
                    '.ui-dropdown__custom button'
                );
                if (
                    !element.validity.valid ||
                    !validCustom ||
                    isInvalidDropdownSelect
                ) {
                    const msg = isInvalidDropdownSelect
                        ? validationLabels.value
                        : this.translateError(element, validationLabels);
                    const errorType = isInvalidDropdownSelect
                        ? 'valueMissing'
                        : this.resolveValidityString(element);
                    element.setAttribute('data-error-type', errorType);
                    element.setAttribute('aria-invalid', 'true');
                    if (customBtn) {
                        customBtn.setAttribute('aria-invalid', 'true');
                    }
                    field.removeErrors(true);
                    if (msg) {
                        const errorMessage =
                            errorType === 'patternMismatch'
                                ? `${msg}${this.getFormatInfoMessage(element)}`
                                : msg;
                        field.addError(errorMessage, errorType, false);
                    }
                } else {
                    if (field && invalidElementsCount === 0) {
                        field.removeErrors(true);
                        element.removeAttribute('aria-invalid');
                        if (customBtn) {
                            customBtn.removeAttribute('aria-invalid');
                        }
                    }
                }
            });
    };

    /**
     * Announces all form error messages for screen readers.
     */
    private announceAllErrors() {
        setTimeout(() => {
            this.getForm()
                .querySelectorAll<HTMLElement>(
                    `ui-hint[type=${UIHint.types.ERROR}]`
                )
                .forEach((hint) => {
                    hint.removeAttribute('role');
                    // Re-setting role triggers the live region announcement again.
                    runInNextFrame(() => hint.setAttribute('role', 'alert'));
                });
        }, FormValidator.AnnouncementTimeout); // makes sure DOM has updated
    }

    /**
     * Announces a single field error to screen readers.
     */
    private announceSingleFieldError = (
        elem: HTMLInputSelectOrTextElement
    ): void => {
        const field = elem.closest<UIField>('ui-field');
        const hint = field?.querySelector<HTMLElement>(
            `ui-hint[type=${UIHint.types.ERROR}]`
        );

        if (!hint) {
            return;
        }

        hint.removeAttribute('role');
        setTimeout(() => {
            hint.setAttribute('role', 'alert');
        }, FormValidator.AnnouncementTimeout); // without timeout attribute change is not detected by screen readers
    };

    // Suffix is used for translation messages.
    getRulesConfig = () => {
        return {
            badInput: {
                suffix: 'input',
            },
            customError: {
                suffix: 'custom',
            },
            patternMismatch: {
                suffix: 'pattern',
                attributes: ['pattern'],
            },
            rangeOverflow: {
                suffix: 'rangeOverflow',
                attributes: ['max'],
            },
            rangeUnderflow: {
                suffix: 'rangeUnderflow',
                attributes: ['min'],
            },
            stepMismatch: {
                suffix: 'step',
                attributes: ['step'],
                formatter: (msg: string, elem: HTMLElement) => {
                    const step: number = Number(elem.getAttribute('step') || 1);
                    const value: number = Number(
                        (elem as HTMLInputElement).value
                    );
                    let low: number | string = value - (value % step);
                    let high: number | string = value + step - (value % step);
                    const precision = String(step).replace(
                        // ^ start of the line
                        // \d* zero of any number of digits
                        // \. zero or one dot
                        /^\d*\.?/,
                        ''
                    ).length;
                    if (precision) {
                        low = low.toFixed(precision);
                        high = high.toFixed(precision);
                    }
                    // @ts-expect-error - using dynamic arguments
                    return this.formatMessage.apply(null, [
                        msg,
                        step,
                        low,
                        high,
                    ]);
                },
            },
            tooLong: {
                suffix: 'long',
                attributes: ['maxlength'],
            },
            tooShort: {
                suffix: 'short',
                attributes: ['minlength'],
            },
            typeMismatch: {
                suffix: 'type',
                attributes: ['type'],
            },
            valueMissing: {
                suffix: 'value',
            },
        };
    };

    getFieldMismatchRule = (): FormValidatorRule => {
        return {
            suffix: 'matches',
            attributes: [],
            formatter: (msg: string): string =>
                this.formatMessage.apply(null, [msg]),

            rule: (elem): boolean => {
                const target = elem?.dataset?.matchesRule
                    ? document.querySelector<HTMLInputElement>(
                          elem.dataset.matchesRule
                      )
                    : undefined;
                if (!target) {
                    return false;
                }
                target.onkeyup = () => {
                    elem.checkValidity();
                };

                if (elem.value !== target.value) {
                    // @ts-expect-error - TS2339: Property _fieldMismatch does not exist on type ValidityState
                    elem.validity._fieldMismatch = true;
                    elem.setCustomValidity(validationLabels.matches);
                } else {
                    // @ts-expect-error - TS2339: Property _fieldMismatch does not exist on type ValidityState
                    elem.validity._fieldMismatch = false;
                    elem.setCustomValidity('');
                }
                // @ts-expect-error - TS2339: Property _fieldMismatch does not exist on type ValidityState
                return !elem.validity._fieldMismatch;
            },
        };
    };

    getCountCheckboxesRule = (): FormValidatorRule => {
        return {
            suffix: 'checkboxes',
            attributes: [],
            formatter: (msg: string, elem: HTMLElement) => {
                const field = elem.closest('ui-field');
                const num = field?.querySelector<HTMLElement>(
                    '[data-checkboxes-rule]'
                )?.dataset?.checkboxesRule;
                const translation = field?.querySelector<HTMLElement>(
                    '[data-checkboxes-error]'
                );
                if (translation?.dataset?.checkboxesError) {
                    msg = translation.dataset.checkboxesError;
                }
                // @ts-expect-error - using dynamic arguments
                return this.formatMessage.apply(null, [msg, num]);
            },

            rule: (elem) => {
                const limit = Number(elem.dataset.checkboxesRule) || 1;
                const field = elem.closest<UIField>('ui-field');
                let checkboxes: UndefinedOr<NodeListOf<HTMLInputElement>>;
                let grouped = false;
                if (elem.dataset.group) {
                    checkboxes = document.querySelectorAll(
                        `[data-group="${elem.dataset.group}"]`
                    );
                    grouped = true;
                } else {
                    checkboxes = field?.querySelectorAll(
                        'input[type="checkbox"]'
                    );
                }

                const checkboxArray = checkboxes ? [...checkboxes] : [];
                const lastCheckbox = checkboxArray[checkboxArray.length - 1];

                const checked: number = checkboxArray.reduce<number>(
                    (out, checkbox) => {
                        return out + Number(checkbox.checked);
                    },
                    0
                );

                const isGroupValid = checked >= limit;

                if (grouped) {
                    // For grouped checkboxes, we show only one error message on the last checkboxes ui-field.
                    const lastField = lastCheckbox.closest<UIField>('ui-field');
                    this.applyGroupCheckboxValidity(
                        checkboxArray,
                        lastCheckbox,
                        isGroupValid
                    );
                    this.updateGroupErrorHint(
                        lastField,
                        checkboxArray,
                        lastCheckbox,
                        isGroupValid,
                        limit
                    );

                    // @ts-expect-error - TS2339: Property _countCheckboxes does not exist on type ValidityState
                    elem.validity._countCheckboxes = false;
                    return true;
                } else {
                    // For non-grouped checkboxes(includes lists with checkboxes), we show error messages on corresponding ui-field.
                    this.applyNonGroupedCheckboxValidity(
                        checkboxArray,
                        elem,
                        isGroupValid
                    );
                }

                // @ts-expect-error - TS2339: Property _countCheckboxes does not exist on type ValidityState
                elem.validity._countCheckboxes = !isGroupValid;

                field?.removeErrors(true);
                // @ts-expect-error - TS2339: Property _countCheckboxes does not exist on type
                return !elem.validity._countCheckboxes;
            },
        };
    };

    private setCheckboxValidity(
        checkbox: HTMLInputElement,
        valid: boolean
    ): void {
        if (valid) {
            checkbox.setCustomValidity('');
            checkbox.removeAttribute('required');
            checkbox.removeAttribute('data-required');
        } else {
            checkbox.setCustomValidity(validationLabels.checkboxes);
            checkbox.setAttribute('required', 'required');
            checkbox.setAttribute('data-required', '1');
        }
    }

    private applyGroupCheckboxValidity(
        checkboxArray: Array<HTMLInputElement>,
        lastCheckbox: HTMLInputElement,
        groupValid: boolean
    ): void {
        checkboxArray.forEach((checkbox) => {
            const isLast = checkbox === lastCheckbox;
            this.setCheckboxValidity(checkbox, groupValid || !isLast);
        });
    }

    private applyNonGroupedCheckboxValidity(
        checkboxArray: Array<HTMLInputElement>,
        elem: HTMLInputSelectOrTextElement,
        groupValid: boolean
    ): void {
        // @ts-expect-error - TS2339: Property _countCheckboxes does not exist on type ValidityState
        elem.validity._countCheckboxes = !groupValid;
        checkboxArray.forEach((checkbox) => {
            this.setCheckboxValidity(checkbox, groupValid);
        });
    }

    private updateGroupErrorHint(
        lastField: UIField | null,
        checkboxArray: Array<HTMLInputElement>,
        lastCheckbox: HTMLInputElement,
        groupValid: boolean,
        limit: number
    ): void {
        if (groupValid) {
            lastField?.removeErrors(true);
        } else {
            const errorMsg = this.formatMessage(
                validationLabels.checkboxes,
                String(limit)
            );
            lastField?.removeErrors(true);
            lastField?.addError(errorMsg, 'checkboxes', false);
        }

        // Re-apply visual error state on non-last fields after drawErrors cleans them up.
        // Using setTimeout to make sure this runs after the drawErrors.
        setTimeout(() => {
            checkboxArray
                .filter((checkbox) => checkbox !== lastCheckbox)
                .forEach((checkbox) => {
                    const checkboxField = checkbox.closest<UIField>('ui-field');
                    if (!checkboxField) {
                        return;
                    }

                    if (groupValid) {
                        checkboxField.removeErrors(true);
                    } else if (
                        !checkboxField.querySelector(
                            'ui-hint[error-type="checkboxes-group-member"]'
                        )
                    ) {
                        checkboxField.addError(
                            '',
                            'checkboxes-group-member',
                            false
                        );
                    }
                });
        }, 0);
    }

    getRequiredMultipleRule = (): FormValidatorRule => {
        return {
            suffix: 'required-multiple',
            attributes: [],
            formatter: (msg: string, elem: HTMLElement) => {
                const field = elem.closest('ui-field');
                const num = field?.querySelector<HTMLElement>(
                    '[data-required-multiple-rule]'
                )?.dataset.requiredMultipleRule;
                const translation = field?.querySelector<HTMLElement>(
                    '[data-required-multiple-error]'
                );
                if (translation?.dataset.requiredMultipleError) {
                    msg = translation.dataset.requiredMultipleError;
                }
                // @ts-expect-error - using dynamic arguments
                return this.formatMessage.apply(null, [msg, num]);
            },
            rule: (elem) => {
                const limit = Number(elem.dataset.requiredMultipleRule) || 1;
                const field = elem.closest<UIField>('ui-field');
                let inputs: UndefinedOr<NodeListOf<HTMLInputElement>>;
                let grouped = false;
                if (elem.dataset.group) {
                    inputs = document.querySelectorAll(
                        `[data-group="${elem.dataset.group}"]`
                    );
                    grouped = true;
                } else {
                    inputs = field?.querySelectorAll('input,select,textarea');
                }

                const inputsArray = inputs ? Array.from(inputs) : [];

                const valid = inputsArray.reduce((out, input) => {
                    return out + Number(!!input.value.trim());
                }, 0);

                inputsArray.forEach((input) => {
                    if (valid >= limit) {
                        // @ts-expect-error - TS2339: Property _requiredMultiple does not exist on type ValidityState
                        elem.validity._requiredMultiple = false;
                        input.setCustomValidity('');
                        input.removeAttribute('required');
                        input.removeAttribute('data-required');
                        if (grouped) {
                            try {
                                const fieldElement =
                                    input.closest<UIField>('ui-field');
                                fieldElement?.removeErrors(true);
                            } catch (e) {
                                console.warn(
                                    elem.toString(),
                                    'is not inside ui-field'
                                );
                            }
                        }
                    } else {
                        // @ts-expect-error - TS2339: Property _requiredMultiple does not exist on type ValidityState
                        elem.validity._requiredMultiple = true;
                        elem.setCustomValidity(
                            validationLabels.requiredMultiple
                        );
                        input.setAttribute('required', 'required');
                        input.setAttribute('data-required', '1');
                        if (grouped && elem !== input) {
                            try {
                                const fieldElement =
                                    input.closest<UIField>('ui-field');
                                const hint =
                                    fieldElement?.querySelector<UIHint>(
                                        'ui-hint'
                                    );
                                if (hint && hint.type === UIHint.types.ERROR) {
                                    hint.remove();
                                }
                                fieldElement?.addError('', '', false);
                            } catch (e) {
                                console.warn(
                                    `${elem.localName} is not inside ui-field.`
                                );
                            }
                        }
                    }
                });
                if (!grouped) {
                    field?.removeErrors(true);
                }
                // @ts-expect-error - TS2339: Property _requiredMultiple does not exist on type ValidityState
                return !elem.validity._requiredMultiple;
            },
        };
    };

    getRemoteValidationRule = (): FormValidatorRule => {
        return {
            suffix: 'remote',
            rule: (elem) => {
                const src = elem.dataset.remoteRule;
                const uidata = document.createElement('ui-data') as UIData;

                if (!elem.validity.valid) {
                    return true;
                } else {
                    // @ts-expect-error - TS2339: Property _remoteValidation does not exist on type ValidityState
                    elem.validity._remoteValidation = true;
                    uidata.type = 'application/json';
                    uidata.src = src as string;
                    uidata.get((data: unknown) => {
                        const resp: FormValidatorResponse =
                            data as FormValidatorResponse;
                        if (resp.valid) {
                            // @ts-expect-error - TS2339: Property _remoteValidation does not exist on type ValidityState
                            elem.validity._remoteValidation = false;
                            elem.setCustomValidity('');
                        } else {
                            // @ts-expect-error - TS2339: Property _remoteValidation does not exist on type ValidityState
                            elem.validity._remoteValidation = true;
                            elem.setCustomValidity(resp.message);
                            this.addForcedErrorForElement(
                                elem as HTMLInputElement,
                                resp.message,
                                'customError'
                            );
                        }
                    });
                }
                // @ts-expect-error - TS2339: Property _remoteValidation does not exist on type ValidityState
                return elem.validity._remoteValidation as FormValidatorRule;
            },
        };
    };

    getOnlyOneOfValidationRule = (): FormValidatorRule => {
        return {
            suffix: 'only-one-of',
            rule: (elem) => {
                let elementsToValidate: Array<HTMLInputElement>;
                const fieldset =
                    elem.closest('fieldset') || elem.closest('ui-fieldset');
                if (fieldset) {
                    elementsToValidate = Array.from(
                        fieldset.querySelectorAll('[data-only-one-of-rule]')
                    );
                } else {
                    const elements =
                        elem.form?.querySelectorAll<HTMLInputElement>(
                            '[data-only-one-of-rule]'
                        );
                    const elementsArray = elements ? [...elements] : [];
                    elementsToValidate = elementsArray.filter((el) => {
                        return (
                            !el.closest('fieldset') &&
                            !el.closest('ui-fieldset')
                        );
                    });
                }
                const result = elementsToValidate
                    .map((el) => !!el.value)
                    .filter((v) => v);

                if (result.length !== 1) {
                    elementsToValidate.forEach((el) => {
                        // @ts-expect-error - TS2339: Property _onlyOneOfValidation does not exist on type ValidityState
                        el.validity._onlyOneOfValidation = true;
                        elem.setCustomValidity(validationLabels.onlyOneOf);
                        el.required = true;
                        const err = this.translateError(el, validationLabels);
                        const field = el.closest<UIField>('ui-field');
                        field?.removeErrors(true);
                        if (err) {
                            this.addForcedErrorForElement(
                                el,
                                err,
                                'customError'
                            );
                        }
                    });
                    return false;
                } else {
                    elementsToValidate.forEach((el) => {
                        // @ts-expect-error - TS2339: Property _onlyOneOfValidation does not exist on type ValidityState
                        el.validity._onlyOneOfValidation = false;
                        el.setCustomValidity('');
                        el.required = false;
                        const field = el.closest<UIField>('ui-field');
                        field?.removeErrors(true);
                    });
                    return true;
                }
                // return !elem.validity._onlyOneOfValidation;
            },
        };
    };

    getMaxFileSizeRule = (): FormValidatorRule => {
        return {
            suffix: 'maxfilesize',
            rule: (elem) => {
                const fsize = Number(elem.dataset.maxfilesizeRule);
                // @ts-expect-error - TS2339: Property _maxfilesize does not exist on type ValidityState
                elem.validity._maxfilesize = false;
                elem.setCustomValidity('');
                const field = elem.closest<UIField>('ui-field');
                if (field) {
                    field?.removeErrors();
                }
                if (elem instanceof HTMLInputElement && elem?.files?.length) {
                    for (const file of elem.files) {
                        if (file.size > fsize) {
                            // @ts-expect-error - TS2339: Property _maxfilesize does not exist on type ValidityState
                            elem.validity._maxfilesize = true;
                            elem.setCustomValidity(
                                validationLabels.maxfilesize
                            );
                            break;
                        }
                    }
                }
                // @ts-expect-error - TS2339: Property _maxfilesize does not exist on type ValidityState
                return !elem.validity._maxfilesize;
            },
            formatter: (msg: string, elem: HTMLElement) => {
                const fileSize = Number(elem.dataset.maxfilesizeRule);
                return this.formatMessage.apply(null, [
                    msg,
                    Math.ceil(fileSize / 1024).toString(),
                ]);
            },
        };
    };

    handleFormUpdate: EventListener = (event: Event) => {
        if (!('detail' in event)) {
            return;
        }

        (event.detail as UpdateFormEventPayload).updatedNodes.forEach(
            (element: HTMLElement) => {
                element.dispatchEvent(
                    new Event(FormValidator.DEFAULT_EVENT_NAME)
                );
            }
        );
    };

    handleSubmit = (_event: Event) => {
        this.submitAttempted = true;
        if (this.beforeSubmit && typeof this.beforeSubmit === 'function') {
            this.beforeSubmit.call(this);
        }
    };

    handleKeyDown = (event: Event) => {
        if (isEnterPressed(event as KeyboardEvent)) {
            if (event.defaultPrevented) {
                return;
            }

            const target = event.target as HTMLElement;
            if (target?.localName === 'select') {
                event.preventDefault();
                return;
            }
            if (!this.isImplicitSubmitTarget(target)) {
                return;
            }

            this.submitAttempted = true;
            this.resolveElementsRequirability();
            const form = this.getForm();
            if ('checkValidity' in form) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                (form as HTMLFormElement).checkValidity();
            }
            this.validate();
            this.triggerDropdownSelectValidation();
            this.focusFirstInvalid();
        }
    };

    /* Determine whether the focused element is a text-like input or submit control that should trigger implicit form submission on ENTER
    (see HTML spec: https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#implicit-submission). */
    private isImplicitSubmitTarget(target: HTMLElement): boolean {
        if (!target) {
            return false;
        }

        const tagName = target.localName;
        if (tagName === 'input') {
            const type = (target as HTMLInputElement).type;
            return (
                FormValidator.IMPLICIT_SUBMIT_INPUT_TYPES.includes(type) ||
                type === 'submit'
            );
        }
        if (tagName === 'button') {
            const type = (target as HTMLButtonElement).type;
            return type === 'submit';
        }

        return false;
    }

    handleInputEvent = (event: Event) => {
        const target = event.target as HTMLInputSelectOrTextElement;
        const invalidEvent = new Event(FormValidator.INVALID_EVENT_NAME);
        if (['input', 'select', 'textarea'].includes(target.localName)) {
            target?.dispatchEvent(invalidEvent);
            // Read assistive error by screen readers.
            const field = target?.closest<UIField>('ui-field');
            field?.announceError();
        }
    };

    /**
     * Handles change events on radio and checkbox inputs.
     * Re-evaluates element requirability after the change takes effect, so that
     * fields shown/hidden by a radio/checkbox selection have their required state
     * updated in the same click (not one click behind).
     */
    handleChangeEvent = (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (
            target?.localName === 'input' &&
            ['radio', 'checkbox'].includes(target.type)
        ) {
            runInNextFrame(() => {
                this.resolveElementsRequirability();
            });
        }
    };

    handlePointerDown = (event: Event) => {
        let target = event.target;
        while (target) {
            const isNonTypedButton =
                target instanceof HTMLButtonElement &&
                target?.tagName === 'BUTTON' &&
                target?.type !== 'button';
            const isInputWithSubmit =
                target instanceof HTMLInputElement &&
                target.tagName === 'INPUT' &&
                target.type === 'submit';
            const isNextButton =
                target instanceof Element &&
                target.classList.contains('-next') &&
                target.classList.contains('button');
            const isUpload =
                target instanceof Element &&
                !target.classList.contains('-upload');

            if (
                isNonTypedButton ||
                isInputWithSubmit ||
                isNextButton ||
                isUpload
            ) {
                this.resolveElementsRequirability();
                this.validate();
                const isButton = (target as Element).tagName === 'BUTTON';
                const isInput = (target as Element).tagName === 'INPUT';
                const isSubmit = 'type' in target && target.type === 'submit';

                if ((isButton || isInput) && isSubmit) {
                    this.submitAttempted = true;
                    // Maybe will be needed to force manual checking.
                    const form = this.getForm();
                    if ('checkValidity' in form) {
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                        (form as HTMLFormElement).checkValidity();
                    }
                    // Dropdown selects are display:none so checkValidity() skips them (willValidate=false).
                    // Manually fire the invalid event so drawErrors runs for them.
                    this.triggerDropdownSelectValidation();
                    this.focusFirstInvalid();
                    this.announceAllErrors();
                }
                break;
            } else {
                if (target instanceof Element) {
                    target = target.parentElement;
                }
            }
        }
    };

    /**
     * Gets or creates new instance of FormValidator for given form.
     * @param {HTMLFormElement} form
     */
    static getInstance(form: HTMLFormElement): FormValidator {
        return (form.validator as FormValidator) ?? new FormValidator(form);
    }

    /**
     * Reset multiple fields value and validation
     * @param {Array<{ element: HTMLElement|HTMLInputElement, value: string|number }>} elements
     */
    static resetFieldsToValue(
        elements: Array<{
            element: HTMLElement | HTMLInputElement;
            value: string | number;
        }>
    ): void {
        elements.forEach(({ element, value }) => {
            if ('value' in element) {
                element.value = String(value);
                element.dispatchEvent(new Event(this.DEFAULT_EVENT_NAME));
            }
        });

        elements.forEach(({ element }) => {
            const field = element.closest<UIField>('ui-field');
            field?.removeErrors(true);
        });
    }

    /**
     * Reset field value and validation
     * @param {HTMLInputSelectOrTextElement} element
     * @param {string | number} value
     */
    static resetFieldToValue(
        element: HTMLInputSelectOrTextElement,
        value: string | number
    ): void {
        element.value = String(value);
        element.dispatchEvent(new Event(this.DEFAULT_EVENT_NAME));
        const field = element.closest<UIField>('ui-field');
        field?.removeErrors(true);
    }

    /**
     * Sets or removes required property and keeps the validator's visibility cache in sync.
     * Use this when required is changed programmatically for a field that may be hidden, to avoid cache-related side effects.
     * @param {HTMLInputSelectOrTextElement} element
     * @param {boolean} required
     */
    setElementRequired(
        element: HTMLInputSelectOrTextElement,
        required: boolean
    ): void {
        if (!required) {
            element.required = false;
            element.removeAttribute('data-required');
            return;
        }

        if (this.isValidationTargetVisible(element)) {
            element.required = true;
            element.removeAttribute('data-required');
            return;
        }

        // If required and hidden, cache the required state
        element.required = false;
        element.setAttribute('data-required', '1');
    }

    /**
     * Get a localised validation error message for a given element
     * @param {HTMLElement|HTMLInputElement} element
     * @returns {boolean}
     */
    static validateField(element: HTMLInputSelectOrTextElement): boolean {
        const field = element.closest<UIField>('ui-field');

        if (element.validity.valid) {
            if (field) {
                const prevError = field.querySelector('ui-hint[error-type]');
                if (prevError) {
                    prevError.parentElement?.removeChild(prevError);
                    field.checkErrors();
                }
            }
            return true;
        }

        if (field) {
            const form = element.closest('form');
            const errorType =
                (form?.validator as FormValidator)?.resolveValidityString(
                    element
                ) ?? this.UIValidationErrorType;

            field.removeErrors(true);
            const message = FormValidator.translateValidationMessage(element);
            if (message) {
                const errorMessage =
                    errorType === 'patternMismatch'
                        ? `${message}${form?.validator.getFormatInfoMessage(element)}`
                        : message;
                field.addError(errorMessage, errorType).checkErrors();
            }
        }

        return false;
    }

    /**
     * Checks validity of a given element, sets / removes errors to related ui-field
     * @param {HTMLElement|HTMLInputElement} element
     * @returns {string}
     */
    static translateValidationMessage(
        element: HTMLInputSelectOrTextElement
    ): UndefinedOr<string> {
        const validator = new FormValidator(document.createElement('form'));
        return validator.translateError(element, validationLabels);
    }

    /**
     * @returns {Function}
     */
    get beforeSubmit(): NullOr<() => void> {
        return this._beforeSubmit || null;
    }

    /**
     * @param {Function} fn
     */
    set beforeSubmit(fn: () => void) {
        this._beforeSubmit = fn;
    }

    /**
     * Some useful validation patterns.
     * Patters should be surrounded by semicolons to make them different
     * from common words and avoid impact.
     * Get more patterns from http://html5pattern.com
     * @type {ValidationPatterns}
     */
    static get PATTERNS(): ValidationPatterns {
        return {
            ':basic_date:':
                '(0[1-9]|1[0-9]|2[0-9]|3[01]).(0[1-9]|1[012]).[0-9]{4}', // DD.MM.YYYY
            ':time_secs:': '(0[0-9]|1[0-9]|2[0-3])(:[0-5][0-9]){2}', // HH:MM:SS
            ':time:': '(0[0-9]|1[0-9]|2[0-3])(:[0-5][0-9])', // HH:MM
            ':credit_card:': '[0-9]{13,16}',
            ':alnum:': '[a-zA-Z0-9]+',
            ':alpha:': '[a-zA-Z]+',
            ':numeric:': '[0-9]+',
            ':numeric_comma_dot:': '[\\-+]?[0-9]*[.,]?[0-9]*', // Format: 9 or 9.9 or 9,9 or 9,99
        };
    }

    /**
     * Matches patterns to human-readable examples of the corresponding formats.
     * E.g:
     *   '(0[1-9]|1[0-9]|2[0-9]|3[01]).(0[1-9]|1[012]).[0-9]{4}' -> DD.MM.YYYY
     *   ':time-secs: -> HH:MM:SS
     * @param {PatternKey | Pattern | null} pattern
     */
    getFormatInfoFor(
        pattern: NullOr<PatternKey | Pattern>
    ): TranslationLabels.Label {
        if (!pattern) {
            return '';
        }
        switch (pattern) {
            case ':basic_date:':
            case '(0[1-9]|1[0-9]|2[0-9]|3[01]).(0[1-9]|1[012]).[0-9]{4}':
                return validationLabels.basicDateFormat;
            case '[0-9]{1,2}\\.[0-9]{1,2}\\.[0-9]{4}':
                return validationLabels.alternativeDateFormat;
            case '[0-9]{4}-[0-9]{2}-[0-9]{2}':
                return validationLabels.reverseDateFormat;
            case ':time_secs:':
            case '(0[0-9]|1[0-9]|2[0-3])(:[0-5][0-9]){2}':
                return validationLabels.basicTimeFormat;
            case ':time:':
            case '(0[0-9]|1[0-9]|2[0-3])(:[0-5][0-9])':
                return validationLabels.shortTimeFormat;
            case ':credit_card:':
            case '[0-9]{13,16}': {
                return validationLabels.digitsAmountFormat === 'string'
                    ? String(validationLabels.digitsAmountFormat).replace(
                          '%s',
                          '13-16'
                      )
                    : validationLabels.digitsAmountHardcodedFormat;
            }
            case ':alnum:':
            case '[a-zA-Z0-9]+':
                return validationLabels.lettersAndNumbersFormat;
            case ':alpha:':
            case '[a-zA-Z]+':
                return validationLabels.lettersOnlyFormat;
            case ':numeric:':
            case '[0-9]+':
                return validationLabels.numbersOnlyFormat;
            case ':numeric_comma_dot:':
            case '[\\-+]?[0-9]*[.,]?[0-9]*':
                return validationLabels.decimalPointNumbersFormat;
            default:
                return '';
        }
    }

    /**
     * Returns a formatted version of the getFormatInfoFor(pattern) output.
     * Leaves a space in front of the message, so it can be added after the general validation message.
     * Returns an empty string if not matching any predefined pattern.
     * @param {HTMLInputSelectOrTextElement} element - element to get the pattern from
     */
    getFormatInfoMessage = (element: HTMLInputSelectOrTextElement) => {
        const formatInfo = this.getFormatInfoFor(
            element.getAttribute('pattern')
        );
        return formatInfo ? ` ${String(formatInfo)}` : '';
    };

    /**
     * Formats and prepares suffix to correct format.
     * @param {string} ruleStr
     * @returns {string}
     * @private
     */
    prepareSuffix(ruleStr: string): string {
        return ruleStr
            .replace('Rule', '')
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase();
    }

    /**
     * Add custom validation rule.
     * @param {string} name - NB! Name of curtome rules should begin with underscore (_).
     * @param {object} object - object should contain following properties:
     *  {@desc suffix: used for translation}
     *  {@desc formatter: function to format error message}
     *  {@desc rule: function to validate condition}
     *  {@desc attributes: used for message formatter}
     */
    addCustomRule(name: string, object: FormValidatorRule): void {
        if (!name.startsWith('_')) {
            name = '_' + name;
        }
        this._RULES[name] = object;
    }

    /**
     * Returns the UIForm where this validation is attached.
     * @returns {UIForm|HTMLFormElement}
     */
    getForm(): UIForm | HTMLFormElement {
        return this._form;
    }

    /**
     * Returns the all defined rules as object.
     * @returns {object}
     */
    getRules(): FormValidatorRules {
        return this._RULES;
    }

    /**
     * Returns rule name by its suffix property.
     * @param {string} suffix
     * @returns {object | undefined}
     */
    getRuleNameBySuffix(suffix: string): UndefinedOr<string> {
        for (const val in this._RULES) {
            if (
                this._RULES.hasOwnProperty(val) &&
                'suffix' in this._RULES[val] &&
                this._RULES[val].suffix === suffix
            ) {
                return val;
            }
        }
        return;
    }

    /**
     * Gets translation suffix by element.
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} elem
     * @returns {string}
     */
    resolveTranslationSuffix(
        elem: HTMLInputSelectOrTextElement
    ): UndefinedOr<string> {
        let validity = this.resolveValidityString(elem);
        if (validity === 'typeMismatch') {
            return elem.type;
        }
        if (validity === 'customError') {
            validity = this.resolveCustomValidityString(elem);
        }
        const rule = this._RULES[validity || 'customError'];
        return rule && 'suffix' in rule ? rule.suffix : undefined;
    }

    /**
     * Get the key for validity.
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} elem
     * @returns {string}
     */
    resolveValidityString(elem: HTMLInputSelectOrTextElement): string {
        for (const key in this._RULES) {
            if (
                this._RULES.hasOwnProperty(key) &&
                elem.validity[key as keyof ValidityState]
            ) {
                return key;
            }
        }
        return '';
    }

    /**
     * Gets the first key for custom validity if in ValidityState -
     * (CustomError is true).
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} elem
     * @returns {string}
     */
    resolveCustomValidityString(
        elem: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    ): string {
        for (const key in elem.validity) {
            if (elem.validity.hasOwnProperty(key) && key.startsWith('_')) {
                return key;
            }
        }
        return '';
    }

    /**
     * Gets translation data-[suffix]-error attribute to replace default
     * translation labels for individual elements.
     * @param {HTMLElement} elem
     * @returns {string | undefined}
     */
    getTranslationAttr(
        elem: HTMLInputSelectOrTextElement
    ): UndefinedOr<string> {
        const rawSuffix = this.resolveTranslationSuffix(elem) ?? '';
        const suffix = rawSuffix
            .toLowerCase()
            .replace(/-([a-z0-9])/g, (_m: string, ch: string) =>
                ch.toUpperCase()
            );
        return elem.dataset[suffix + 'Error'];
    }

    /**
     * Translates validity for element.
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} elem
     * @param {object} labels Collection of labels.
     * @returns {string}
     */
    translateError(
        elem: HTMLInputSelectOrTextElement,
        labels: TranslationLabels.Labels
    ): UndefinedOr<string> {
        const suffix = this.resolveTranslationSuffix(elem);
        let translation = '';
        if (elem.validity.customError) {
            translation =
                this.getTranslationAttr(elem) || elem.validationMessage;
        } else {
            const translationAttr = this.getTranslationAttr(elem);
            const suffixLabel = suffix && labels[suffix];
            const errorMessage = translationAttr || suffixLabel;
            if (errorMessage) {
                translation = errorMessage as string;
            }
        }
        return this.formatErrorMessage(elem, translation);
    }

    /**
     * Resolves attributes from validity rule.
     * @param {string} validity
     * @returns {Array<string>}
     */
    resolveAttributes(
        validity: keyof FormValidatorRules | string
    ): Array<string> {
        const rule = this._RULES[validity];
        const attributes =
            rule && 'attributes' in rule ? rule.attributes : undefined;

        return attributes ?? [];
    }

    /**
     * Very-very shorten version of C's sprintf.
     * Accepts only '%s' for strings.
     * Uses dynamic params - words to replace the %s.
     * @param {string} msg - message with %s placeholders in it
     * @param {...string} args - message with %s placeholders in it
     * @returns {string} - original msg with %s replaced with strings from arguments
     */
    formatMessage(msg: string, ...args: Array<string>): string {
        for (const arg of args) {
            msg = msg.replace('%s', arg);
        }
        return msg;
    }

    /**
     * Default message formatter for validities which hasn't message formatter
     * defined.
     * @param {string} msg Message for error.
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} elem Invalid element.
     * @returns {string}
     */
    defaultMessageFormatter(
        msg: string,
        elem: HTMLInputSelectOrTextElement
    ): string {
        const validity = this.resolveValidityString(elem);
        const attrsToReplace = this.resolveAttributes(validity);
        const attrs: Array<string> = attrsToReplace.map((attrName: string) => {
            return elem.getAttribute(attrName) || '';
        });
        return this.formatMessage.apply(null, [msg, ...attrs]);
    }

    /**
     * Resolves formatter function for validity.
     * @param {HTMLElement} elem
     * @returns {Function | undefined}
     */
    resolveMessageFormatter(
        elem: HTMLInputSelectOrTextElement
    ): UndefinedOr<FormatterFn> {
        const standardKey = this.resolveValidityString(elem);
        const customKey = this.resolveCustomValidityString(elem);

        const hasFormatterForValidityKey = (key: string) =>
            key && Object.hasOwn(this._RULES[key], 'formatter');

        if (hasFormatterForValidityKey(standardKey)) {
            return this._RULES[standardKey].formatter?.bind(this);
        } else if (hasFormatterForValidityKey(customKey)) {
            return this._RULES[customKey]?.formatter?.bind(this);
        } else {
            return this.defaultMessageFormatter.bind(this);
        }
    }

    /**
     * Gets the rule for custom validity.
     * @param {string} validity
     * @returns {Function | undefined | null} - null if no proper validity, undefined if no rule
     */
    resolveCustomRule(
        validity: UndefinedOr<string>
    ): NullOr<UndefinedOr<RuleFn>> {
        return validity && typeof validity !== 'function'
            ? this._RULES[validity]?.rule
            : null;
    }

    /**
     * Attach validator to all elements needs to be validated.
     * Sets them disabled while element isn't visible.
     */
    validate(): void {
        const form = this.getForm();
        if (!(form as HTMLFormElement).elements) {
            return;
        }
        Array.from((form as HTMLFormElement).elements).forEach(
            (elem: Element) => {
                const isInputElement = (
                    element: Element
                ): element is
                    | HTMLTextAreaElement
                    | HTMLSelectElement
                    | HTMLInputElement =>
                    [
                        HTMLTextAreaElement,
                        HTMLSelectElement,
                        HTMLInputElement,
                    ].some((inputType) => element instanceof inputType);
                if (
                    isInputElement(elem) &&
                    'type' in elem &&
                    !['hidden', 'button', 'submit'].includes(elem.type) &&
                    !elem.classList.contains('-utility')
                ) {
                    this.appendElementToValidation(elem);
                    // <ui-duplicate> case
                    if ('dataset' in elem) {
                        if (elem.closest('ui-duplicate')) {
                            delete elem.dataset.validated;
                        } else {
                            elem.dataset.validated ??= 'true';
                        }
                    }
                }
            }
        );
    }

    /**
     * Temporarily removes required from hidden controls and restores it when
     * controls that were cached as required become visible again.
     */
    resolveElementsRequirability(): void {
        const form = this.getForm();
        if (!(form as HTMLFormElement).elements) {
            return;
        }
        [].forEach.call(
            (form as HTMLFormElement).elements,
            (elem: HTMLElement) => {
                const hasForcedValidation =
                    'dataset' in elem &&
                    (elem as HTMLInputElement).dataset.forceValidation;
                const isElementVisible = this.isValidationTargetVisible(elem);
                if (
                    !isElementVisible &&
                    (elem.dataset.required ||
                        ('required' in elem && elem.required))
                ) {
                    (elem as HTMLInputElement).required = false;
                    elem.setAttribute('data-required', '1');
                }
                if (isElementVisible && elem.dataset.required) {
                    (elem as HTMLInputElement).required = true;
                    // Clear the cache after restoring — prevents stale data-required
                    // from re-adding required if it is externally removed later.
                    elem.removeAttribute('data-required');
                }
                if (hasForcedValidation) {
                    (elem as HTMLInputElement).required = true;
                }
            }
        );
    }

    /**
     * Selects inside ui-dropdown are display:none by design; check the dropdown's visibility instead.
     * @param {HTMLElement} elem
     */
    private isValidationTargetVisible(elem: HTMLElement): boolean {
        const dropdown = elem.closest('ui-dropdown');
        const visibilityTarget = (dropdown ?? elem) as HTMLElement;
        return visibilityTarget.checkVisibility();
    }

    /**
     * Handles blur on a checkbox. Triggers validation only when focus leaves
     * the entire checkbox group (or the field when ungrouped), or when at
     * least one checkbox in the group is already checked.
     * @param event
     */
    private handleCheckboxBlur(event: FocusEvent): void {
        const target = event.target as HTMLInputElement;
        const relatedTarget = event.relatedTarget as Element | null;

        const checkboxes = this.getGroupCheckboxes(target);
        const anyChecked = checkboxes.some((cb) => cb.checked);

        // If at least one is checked, validate immediately.
        if (anyChecked) {
            target.checkValidity();
            return;
        }

        // Otherwise, only validate if focus is leaving the group entirely.
        if (relatedTarget) {
            const isStillInGroup = checkboxes.some(
                (cb) => cb === relatedTarget || cb.contains(relatedTarget)
            );
            if (isStillInGroup) {
                return;
            }
        }

        target.checkValidity();
    }

    /**
     * Returns all checkboxes belonging to the same group as the given checkbox.
     * For grouped checkboxes, queries by `data-group` attribute; otherwise returns
     * all checkboxes within the closest `ui-field`.
     * @param {HTMLInputElement} checkbox
     * @returns {Array<HTMLInputElement>}
     */
    private getGroupCheckboxes(
        checkbox: HTMLInputElement
    ): Array<HTMLInputElement> {
        if (checkbox.dataset.group) {
            return Array.from(
                document.querySelectorAll<HTMLInputElement>(
                    `[data-group="${checkbox.dataset.group}"]`
                )
            );
        }
        const field = checkbox.closest('ui-field');
        if (field) {
            return Array.from(
                field.querySelectorAll<HTMLInputElement>(
                    'input[type="checkbox"]'
                )
            );
        }
        return [checkbox];
    }

    /**
     * Attach single element to validator. If there are custom events
     * they will be triggered. If there aren't any custom event specified
     * in attribute 'data-validation-events', then default events will be
     * triggered like 'change' and 'blur'.
     * @param {HTMLElement} elem
     */
    appendElementToValidation(elem: HTMLElement): void {
        if (elem.dataset.validated || elem.dataset.novalidate) {
            return;
        }
        const errorPlacer = this.errorPlacementCallback;
        elem.addEventListener(FormValidator.INVALID_EVENT_NAME, errorPlacer);
        // Assign custom validation events if they exist.
        if (elem.dataset.validationEvents) {
            const events = elem.dataset.validationEvents.split(/\s+/);
            events.forEach((eventName) => {
                elem.addEventListener(eventName, errorPlacer);
            });
        } else {
            elem.addEventListener(
                FormValidator.DEFAULT_EVENT_NAME,
                errorPlacer
            );
            elem.addEventListener('blur', (event: FocusEvent) => {
                const target = event.target as HTMLInputSelectOrTextElement;
                if (
                    target instanceof HTMLInputElement &&
                    target.type === 'checkbox'
                ) {
                    this.handleCheckboxBlur(event);
                } else {
                    if (target?.value) {
                        target.checkValidity();
                    }
                }
                this.announceSingleFieldError(target);
            });
            elem.addEventListener('focusin', (event: FocusEvent) => {
                this.announceSingleFieldError(
                    event.target as HTMLInputSelectOrTextElement
                );
            });
        }
        this.replacePatterns(elem);
    }

    /**
     * Dispatches the `invalid` event on required dropdown selects with an empty value.
     * Needed because display:none selects are skipped by form.checkValidity().
     * @private
     */
    private triggerDropdownSelectValidation(): void {
        this.getForm()
            .querySelectorAll<HTMLSelectElement>('ui-dropdown select')
            .forEach((select) => {
                if (select.required && select.value === '') {
                    select.dispatchEvent(
                        new Event(FormValidator.INVALID_EVENT_NAME)
                    );
                }
            });
    }

    /**
     * Focuses the first invalid element.
     * For ui-dropdown, the native select is hidden so we focus the visible control button instead.
     */
    focusFirstInvalid(): void {
        // Dropdown selects are display:none so they don't match :invalid; check them separately.
        let invalidElement =
            this.getForm().querySelector<HTMLElement>(':invalid');

        // Find first :invalid focusable element inside :invalid fieldset
        if (
            invalidElement &&
            !['input', 'select', 'textarea', 'button'].includes(
                invalidElement.localName
            )
        ) {
            invalidElement =
                invalidElement.querySelector<HTMLElement>(
                    'input:invalid, select:invalid, textarea:invalid'
                ) ?? invalidElement;
        }

        if (!invalidElement) {
            const invalidDropdownSelect = (
                this.getForm() as HTMLFormElement
            ).querySelector<HTMLSelectElement>('ui-dropdown select');
            if (
                invalidDropdownSelect?.required &&
                !invalidDropdownSelect.value
            ) {
                invalidElement = invalidDropdownSelect;
            }
        }

        if (!invalidElement || invalidElement === document.activeElement) {
            return;
        }
        const dropdown = invalidElement.closest('ui-dropdown');
        const focusTarget =
            dropdown?.querySelector<HTMLElement>('.ui-dropdown__control') ??
            invalidElement;
        runInNextFrame(() =>
            focusTarget.focus({
                preventScroll: false,
                ...({ focusVisible: true } as FocusOptions),
            })
        );
    }

    /**
     * Replaces predefined RegEx pattern.
     * @param {HTMLElement} elem
     * @private
     */
    replacePatterns(elem: HTMLElement): void {
        if (elem.hasAttribute('pattern')) {
            const pattern = elem.getAttribute('pattern');
            if (pattern && FormValidator.PATTERNS[pattern as PatternKey]) {
                elem.setAttribute(
                    'pattern',
                    FormValidator.PATTERNS[pattern as PatternKey]
                );
            }
        }
    }

    /**
     * Formats error message depending on field.
     * @param {HTMLElement} elem
     * @param {string} msg
     * @returns {string}
     * @private
     */
    formatErrorMessage(
        elem: HTMLInputSelectOrTextElement,
        msg: string
    ): string {
        const formatter = this.resolveMessageFormatter(elem);
        if (!formatter) {
            return '';
        }
        return formatter(msg, elem);
    }

    /**
     * Force add error to an element.
     * @param {HTMLInputElement} elem HTML field.
     * @param {string} msg Error message.
     * @param {string} type Error type for error message.
     * @returns {boolean}
     */
    addForcedErrorForElement(
        elem: HTMLInputElement,
        msg: string,
        type: string
    ): boolean {
        const field = elem.closest<UIField>('ui-field');
        if (field) {
            field.removeErrors().addError(msg, type);
            return true;
        }
        return false;
    }

    /**
     * Checks that form should be validated.
     * @returns {boolean}
     */
    shouldFormValidate(): boolean {
        return !(this.getForm() as HTMLFormElement).noValidate;
    }
}
